import { useEffect, useState } from 'react';
import { Spinner } from '@/components/Spinner';
import { Pager } from '@/components/Pager';
import { SearchBox } from '@/components/form/SearchBox';
import { CustomSelect } from '@/components/form/CustomSelect';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { useAsyncData } from '@/hooks/useAsyncData';
import { showToast } from '@/lib/toast';
import { fetchAds, createAd, updateAd } from '@/api/ads';
import { AdForm } from './AdForm';
import { AdTile } from './AdTile';
import type { AdFormMode } from './MarketingPage';

/** Фильтр по статусу показа; пустое значение — все записи. */
const STATUS_OPTIONS = [
  { value: '', label: 'Все статусы' },
  { value: '1', label: 'Включённые' },
  { value: '0', label: 'Отключённые' },
];

/**
 * Вкладка "Объявления" раздела «Маркетинг» — 1:1 старые Orchid-экраны
 * AdListScreen/AdEditScreen (см. докблок AdController в Laravel).
 * Раскрывающиеся плитки вместо таблицы и отдельный экран создания/
 * редактирования — тот же приём, что у «Операторов данных».
 *
 * Режим формы приходит сверху (MarketingPage): кнопки «Добавить»/«Удалить»
 * стоят в шапке страницы, над табами.
 */
export function AdsTab({
  form,
  onOpenForm,
  onCloseForm,
  reloadToken,
  onReload,
}: {
  form: AdFormMode;
  onOpenForm: (form: AdFormMode) => void;
  onCloseForm: () => void;
  reloadToken: number;
  onReload: () => void;
}) {
  const [search, searchInput, setSearchInput] = useDebouncedSearch();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  /** Раскрыто ровно одно объявление: раскрытие другого схлопывает предыдущее. */
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const formOpen = form !== null;

  useEffect(() => {
    setPage(1);
  }, [search, status]);

  // Открытая форма занимает всё место списка — грузить его в это время незачем.
  const {
    data: list,
    error,
    setData,
  } = useAsyncData(
    formOpen ? null : () => fetchAds({ search: search || undefined, active: status || undefined, page }),
    [search, status, page, reloadToken, formOpen],
    'Не удалось загрузить список объявлений.',
  );
  const rows = list?.items ?? null;
  const meta = list?.meta ?? null;

  if (form?.mode === 'create') {
    return (
      <AdForm
        onCancel={onCloseForm}
        onSave={async (payload) => {
          const created = await createAd(payload);
          onCloseForm();
          showToast(`Объявление «${created.name}» создано`);
          onReload();
        }}
      />
    );
  }

  if (form?.mode === 'edit') {
    const editing = form.ad;
    return (
      <AdForm
        initial={editing}
        onCancel={onCloseForm}
        onSave={async (payload) => {
          const updated = await updateAd(editing.id, payload);
          onCloseForm();
          showToast(`Объявление «${updated.name}» обновлено`);
          onReload();
        }}
      />
    );
  }

  return (
    <>
      <div className="filter-row">
        <SearchBox value={searchInput} onChange={setSearchInput} placeholder="Поиск по названию" />
        <div className="group-select">
          <CustomSelect value={status} options={STATUS_OPTIONS} onChange={setStatus} />
        </div>
      </div>

      {error && <p className="field-error">{error}</p>}

      {rows === null ? (
        <div className="panel-loading">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <p className="cell-muted">Объявления не найдены.</p>
      ) : (
        <div className="catalog-tiles">
          {rows.map((row) => (
            <AdTile
              key={row.id}
              ad={row}
              expanded={expandedId === row.id}
              onToggleExpand={() => setExpandedId((id) => (id === row.id ? null : row.id))}
              onEditRequest={() => onOpenForm({ mode: 'edit', ad: row })}
              onDeleted={onReload}
              onPatched={(updated) =>
                setData((prev) =>
                  prev ? { ...prev, items: prev.items.map((r) => (r.id === updated.id ? updated : r)) } : prev,
                )
              }
            />
          ))}
        </div>
      )}

      {meta && meta.lastPage > 1 && (
        <div className="table-footer">
          <span className="table-footer__status">
            Страница {meta.currentPage} из {meta.lastPage} · всего {meta.total}
          </span>
          <Pager page={meta.currentPage} lastPage={meta.lastPage} onPageChange={setPage} />
        </div>
      )}
    </>
  );
}
